import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import type { MediaLocation } from '../media/mediaFileName';
import { toDayString } from '../../utils/formatDate';

/** The description refs a Probe holds — one Planum or one Profil. */
interface SampleRefs {
    planumIds: readonly string[];
    profileIds: readonly string[];
}

/** A Probe's own Lage — typed in when it was taken from no description. */
interface SampleOwnLocation {
    area: string;
    planum: string;
    profile: string;
}

/**
 * What a Probe's pictures are named after: the Lage of the description it was
 * taken from, then its day — "…+Bef_7000-Fl_1-Pl_1-Probe_2026_10_03". With no
 * description its own Lage stands in, the Planum before the Profil (a name
 * holds only one); with neither only the day.
 */
export function sampleMediaLocation(
    recordedAt: string,
    refs: SampleRefs,
    findings: readonly FindingDocType[],
    planums: readonly FindingPlanumDocType[],
    profiles: readonly FindingProfileDocType[],
    ownLocation?: SampleOwnLocation
): MediaLocation {
    const planum = planums.find((candidate) =>
        refs.planumIds.includes(candidate.id)
    );
    const profile = planum
        ? undefined
        : profiles.find((candidate) => refs.profileIds.includes(candidate.id));
    const description = planum ?? profile;
    const findingNumber = description
        ? findings.find((finding) => finding.id === description.findingId)
              ?.findingNumber
        : undefined;
    if (!description && ownLocation) {
        const ownPlanum = ownLocation.planum.trim();
        return {
            area: ownLocation.area.trim() || undefined,
            planumNumber: ownPlanum || undefined,
            profileNumber: ownPlanum
                ? undefined
                : ownLocation.profile.trim() || undefined,
            sampleDate: toDayString(recordedAt) || undefined
        };
    }
    return {
        findingNumber,
        area: description?.area,
        planumNumber: planum?.planumNumber,
        profileNumber: profile?.profileNumber,
        sampleDate: toDayString(recordedAt) || undefined
    };
}
